import { defineComponent as _defineComponent } from 'vue'
import Modal from "../Modal.vue";
  import Cleave from "vue-cleave-component";
  import { getCurrencyOptions as getCleaveInputOptions } from "@/helpers/cleavePlugin";
  import DatePicker from "../DatePicker.vue";
  import CheckboxInput from "../input/CheckboxInput.vue";
  import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue";
  import { formatDateDayMonthYear } from "@/helpers/date";
  import { fromSpacedToCamelCase } from "@/helpers/transformation/string";
  import { parseStringToNumber as clearNumberPrefixAndParse } from "@/helpers/transformation/number";
  import { EoiInterface } from "@/types/EoiInterface";
  import DropDown from "../dropdown/DropDown.vue";
  import {
    required,
    requiredIf,
    minValue,
    numeric,
    minLength,
    maxLength,
  } from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import { DocusignTemplateInterface } from "@/types/DocusignTemplateInterface";
  import store from "@/store";
  import router from "@/router";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import { redirectWithoutRouter } from "@/helpers/redirectWithoutRouter";
  import { useToast } from "vue-toastification";
  import { useDocusignDepositDetails } from "@/features/docusign/useDocusignDepositDetails";
  import { formatPrice as formatNumber } from "@/helpers/transformation/price";
  import { StatesEnum } from "@/enums/StatesEnum";
  const EARLY_SETTLEMENT_DISCOUNT = "Early settlement discount";
  const SUBJECT_TO_FINANCE = "Subject to finance";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'GenerateContractFieldsModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    eoi: {
      type: Object as () => EoiInterface,
      required: true,
    },
  },
  emits: ["close", "success"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const emits = __emit;

  const props = __props;

  const depositPercentage = ref(5);
  const loading = ref(false);
  const isRedirectValid = ref(false);
  const template = ref<DocusignTemplateInterface>();
  const templatesList = ref<DocusignTemplateInterface[]>([]);

  const datePickerPlaceholder = formatDateDayMonthYear(new Date());
  const cleaveInputOptions = getCleaveInputOptions({
    numeralPositiveOnly: false,
  });

  const toast = useToast();

  const additionalTerms = ref([
    { label: "Deposit bond", isSelected: false },
    { label: "Bank guarantee", isSelected: false },
    { label: EARLY_SETTLEMENT_DISCOUNT, isSelected: false },
    { label: SUBJECT_TO_FINANCE, isSelected: false },
  ]);

  const defaultRules = {
    required,
    numeric,
    minValue: minValue(1),
  };

  const conditionallyRequiredRule = (conditionLabel: string) => ({
    required: requiredIf(() => {
      return additionalTerms.value.some(
        ({ label, isSelected }) => label === conditionLabel && isSelected
      );
    }),
  });

  const rules = computed(() => ({
    streetAddress: { required },
    depositDetails: {
      totalDeposit: defaultRules,
      paid: {
        ...defaultRules,
        minValue: minValue(0),
      },
      balance: {
        ...defaultRules,
        minValue: minValue(0),
      },
      gst: { required },
      gstAmount: { required: requiredIf(() => form.depositDetails.gst) },
    },
    postcode: { numeric },
    lotNumber: { required },
    size: defaultRules,
    suburb: { required },
    state: { required },
    settlementDate: { required },
    depositBond: { required },
    bankGuarantee: { required },
    earlySettlementDiscount: {
      amount: {
        ...conditionallyRequiredRule(EARLY_SETTLEMENT_DISCOUNT),
      },
    },
    subjectToFinance: {
      loanAmount: {
        ...conditionallyRequiredRule(SUBJECT_TO_FINANCE),
      },
      financeDueDate: conditionallyRequiredRule(SUBJECT_TO_FINANCE),
    },
  }));

  const createFormFields = () => ({
    streetAddress: "",
    depositDetails: {
      totalDeposit: 0,
      paid: props.eoi?.depositAmount ?? 0,
      balance: 0,
      gst: true,
      gstAmount: 0,
    },
    lotNumber: props.eoi.lotId.title,
    size: formatNumber(props.eoi.lotId.size, false),
    suburb: "",
    state: "" as StatesEnum,
    postcode: "",
    settlementDate: new Date(),
    depositBond: false,
    bankGuarantee: false,
    earlySettlementDiscount: {
      amount: null,
    },
    subjectToFinance: {
      loanAmount: 0,
      financeDueDate: new Date(),
    },
  });

  const form = reactive(createFormFields());
  const v$ = useVuelidate(rules, form);
  const {
    calculateDepositDetails,
    onChangeBalance,
    onChangeTotalDepositChange,
    onSelectdepositPercentage,
  } = useDocusignDepositDetails(
    form,
    depositPercentage.value,
    props.eoi.lotId.price
  );

  const fillValues = (eoi: EoiInterface) => {
    const { lotId: lot, contractConditions } = eoi;
    const { settlementDate = null, financeDueDate = null, price } = lot;

    form.subjectToFinance.loanAmount = contractConditions?.landLoanAmount ?? 0;
    form.depositDetails.totalDeposit = price;
    form.subjectToFinance.financeDueDate = financeDueDate
      ? new Date(financeDueDate)
      : new Date();
    form.settlementDate = settlementDate
      ? new Date(settlementDate)
      : new Date();

    if (contractConditions.subjectToFinance) {
      additionalTerms.value = additionalTerms.value.map((term) =>
        term.label === SUBJECT_TO_FINANCE ? { ...term, isSelected: true } : term
      );
    }

    calculateDepositDetails();
  };

  const getSelectedAdditionalTerms = () => {
    return additionalTerms.value.reduce(
      (acc, { label, isSelected }) =>
        isSelected ? { ...acc, [fromSpacedToCamelCase(label)]: true } : acc,
      {}
    );
  };

  const getTemplates = () => {
    if (!store.state.docusign.userData.accessToken) return;

    store.dispatch("docusign/getTemplates").then(({ data }) => {
      const templates = data.envelopeTemplates.filter(
        ({ name }: DocusignTemplateInterface) => name
      );

      templatesList.value = templates;
    });
  };

  const formatSubmitRequest = () => {
    const isSubjectToFinanceSelected = additionalTerms.value.some(
      ({ label, isSelected }) => label === SUBJECT_TO_FINANCE && isSelected
    );

    const additionalFields = {
      ...form,
      ...form.depositDetails,
      ...getSelectedAdditionalTerms(),
      earlySettlementDiscount: {
        ...form.earlySettlementDiscount,
      },
      subjectToFinance: isSubjectToFinanceSelected ? form.subjectToFinance : {},
      gstAmount: form.depositDetails.gst ? form.depositDetails.gstAmount : null,
    };

    return {
      templateId: template.value?.templateId,
      eoiId: router.currentRoute.value.params.id,
      additionalFields,
    };
  };
  const validateRequest = () => {
    v$.value.$touch();
    const isFormValid = !v$.value.$invalid;

    if (!isFormValid || loading.value) return false;

    if (!template.value?.templateId) {
      toast.error("Please select template");
      return false;
    }

    return true;
  };
  const submit = async () => {
    const canSubmit = validateRequest();

    if (!canSubmit) return;

    loading.value = true;
    sessionStorage.removeItem("openGenerateContract");
    store
      .dispatch("docusign/createEnvelope", formatSubmitRequest())
      .then((response) => {
        isRedirectValid.value = true;
        redirectWithoutRouter(response.data, "_blank");
        onSuccess();
      })
      .catch(() => {
        toast.error("Something went wrong, please try again later");
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const clearForm = () => Object.assign(form, createFormFields());
  const onClose = () => {
    emits("close");
    clearForm();
    resetVuelidateErrors(v$);
  };

  const onSuccess = () => {
    emits("success");
    onClose();
  };

  watch(
    () => props.eoi,
    () => {
      fillValues(props.eoi);
    },
    { deep: true }
  );

  onMounted(() => {
    getTemplates();
    fillValues(props.eoi);
  });

  onUnmounted(() => {
    resetVuelidateErrors(v$);
    clearForm();
    sessionStorage.removeItem("openGenerateContract");
  });

const __returned__ = { emits, props, depositPercentage, loading, isRedirectValid, template, templatesList, EARLY_SETTLEMENT_DISCOUNT, SUBJECT_TO_FINANCE, datePickerPlaceholder, cleaveInputOptions, toast, additionalTerms, defaultRules, conditionallyRequiredRule, rules, createFormFields, form, v$, calculateDepositDetails, onChangeBalance, onChangeTotalDepositChange, onSelectdepositPercentage, fillValues, getSelectedAdditionalTerms, getTemplates, formatSubmitRequest, validateRequest, submit, clearForm, onClose, onSuccess, Modal, get Cleave() { return Cleave }, get getCleaveInputOptions() { return getCleaveInputOptions }, DatePicker, CheckboxInput, ref, reactive, computed, watch, onMounted, onUnmounted, get formatDateDayMonthYear() { return formatDateDayMonthYear }, get fromSpacedToCamelCase() { return fromSpacedToCamelCase }, get clearNumberPrefixAndParse() { return clearNumberPrefixAndParse }, get EoiInterface() { return EoiInterface }, DropDown, get required() { return required }, get requiredIf() { return requiredIf }, get minValue() { return minValue }, get numeric() { return numeric }, get minLength() { return minLength }, get maxLength() { return maxLength }, get useVuelidate() { return useVuelidate }, get DocusignTemplateInterface() { return DocusignTemplateInterface }, get store() { return store }, get router() { return router }, get resetVuelidateErrors() { return resetVuelidateErrors }, get redirectWithoutRouter() { return redirectWithoutRouter }, get useToast() { return useToast }, get useDocusignDepositDetails() { return useDocusignDepositDetails }, get formatNumber() { return formatNumber }, get StatesEnum() { return StatesEnum } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})